import { Box, IconButton, InputAdornment, Menu } from "@mui/material";
import { useRef, useState } from "react";
import { Controller, ControllerRenderProps } from "react-hook-form";

import { MenuItem, TextField } from "@/components/ui-v2";

import {
  EXPORT_FORM_FIELDS,
  SIZE_MAX_PERCENT,
  SIZE_MIN_PERCENT,
  SIZE_PRESETS,
  SIZE_STEP_PERCENT,
} from "../../../constants";

interface SizePercentFieldProps {
  onSizeChange: (value: string, field: ControllerRenderProps) => void;
}

export const SizePercentField = (props: SizePercentFieldProps) => {
  const { onSizeChange } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Controller
      name={EXPORT_FORM_FIELDS.EXPORT_SIZE}
      render={({ field, fieldState: { error } }) => {
        const percentValue = Math.round(field.value * 100);

        const applyPercentChange = (newPercent: number) => {
          const clamped = Math.min(
            Math.max(newPercent, SIZE_MIN_PERCENT),
            SIZE_MAX_PERCENT,
          );
          onSizeChange((clamped / 100).toString(), field);
        };

        return (
          <Box ref={containerRef} sx={{ pt: 6 }}>
            <TextField
              error={!!error}
              fullWidth
              helperText={error?.message}
              label="Size"
              size="small"
              sx={spinnerStyles}
              onChange={(event) => {
                const val = Number(event.target.value);
                if (!Number.isNaN(val) && val > 0) {
                  onSizeChange((val / 100).toString(), field);
                }
              }}
              onBlur={() => {
                field.onBlur();
                applyPercentChange(percentValue);
              }}
              slotProps={{
                htmlInput: {
                  min: SIZE_MIN_PERCENT,
                  max: SIZE_MAX_PERCENT,
                  step: SIZE_STEP_PERCENT,
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      %
                      <IconButton
                        size="small"
                        onClick={() => setIsMenuOpen(true)}
                        disableRipple
                        sx={dropdownButtonStyles}
                      >
                        ▾
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                inputLabel: {
                  shrink: true,
                },
              }}
              type="number"
              value={percentValue}
            />
            <Menu
              anchorEl={containerRef.current}
              open={isMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: "unset",
                    width: containerRef.current?.offsetWidth,
                    maxHeight: 240,
                  },
                },
              }}
            >
              {SIZE_PRESETS.map((preset) => (
                <MenuItem
                  key={preset}
                  selected={preset === percentValue}
                  onClick={() => {
                    applyPercentChange(preset);
                    setIsMenuOpen(false);
                  }}
                  sx={menuItemStyles}
                >
                  {preset}%
                </MenuItem>
              ))}
            </Menu>
          </Box>
        );
      }}
    />
  );
};

const spinnerStyles = {
  "& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": {
    WebkitAppearance: "inner-spin-button",
    opacity: 1,
  },
  "& input[type=number]": {
    MozAppearance: "spinner-textfield",
  },
};

const dropdownButtonStyles = {
  p: 0,
  mr: -1,
  mt: "-3px",
  fontSize: 24,
  lineHeight: 1,
  "&:hover": {
    background: "none",
  },
};

const menuItemStyles = {
  py: 0.5,
  minHeight: "unset",
  fontSize: 13,
};
